import {
  Flex,
  BoxProps,
  Grid,
  GridItem,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Dispatch, SetStateAction, useEffect, useState, useRef } from 'react';
import { UseMutateFunction } from '@tanstack/react-query';

import {
  SetCarPartMutation,
  MutationSetCarPartsArgs,
} from 'api/generated/graphql';
import {
  IconTyreC1,
  IconTyreC2,
  IconTyreC3,
  IconTyreC4,
  IconTyreC5,
  IconTyreInter,
  IconTyreWet,
  IconInfo,
} from 'icons';
import { SVGIcon, SetupSuggestions, Tyres } from 'types';
import { useGetCarParts, useTuningForRace } from 'hooks';
import { useTuning } from 'context';
import {
  recommendedTyreGradient,
  tyreGradient,
} from 'utils/themeConstants';
import CustomTooltip from 'components/Tooltip/CustomTooltip';


export const TyreCard = ({
  heading,
  subheading,
  icon: Icon,
  isSelected,
  label,
  isSuggested = false,
  ...rest
}: BoxProps & {
  heading: string;
  subheading?: string;
  label?: string;
  icon: SVGIcon;
  isSelected: boolean;
  isSuggested?: boolean;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <GridItem
      alignItems="center"
      justifyContent="center"
    >
      <Flex
        pos="relative"
        h="3.625rem"
        w="full"
        borderWidth="0.094rem"
        borderRadius="0.25rem"
        transition="all 0.2s ease-in-out"
        mb="1"
        _hover={
          isSelected 
            ? undefined
            : {
                borderColor: 'white.20',
                bg: recommendedTyreGradient,
              }
        }
        borderColor={
          isSelected 
            ? 'white.80'
            : 'white.10'
        }
        alignItems="center"
        justifyContent="center"
        bg={
          isSelected 
            ? recommendedTyreGradient
            : tyreGradient
        }
        style={{
          backgroundOrigin: 'border-box'
        }}
        {...rest}
      >
        <Icon w="2.625rem" h="2.625rem" />
        {isSuggested && (
          <CustomTooltip label={label} isOpen={isOpen}>
            <Flex
              position="absolute"
              h="full"
              alignItems="flex-start"
              justifyContent="center"
              style={{
                width: `100%`,
                height: `16px`,
              }}
              background="linear-gradient(180deg, rgba(0, 255, 232, 0.096) 0%, rgba(0, 255, 232, 0) 100%)"
              borderTopRadius="4px"
              opacity={1}
              top="-5"
              zIndex="100"
              onMouseEnter={onOpen}
              onMouseLeave={onClose}
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
            >
              <IconInfo 
                w="0.625rem"
                h="0.625rem"
                mt="0.25rem"
                __css={{
                  path: {
                    fill: 'rgba(0, 255, 232, 1)',
                  },
                }}
              />
            </Flex>
          </CustomTooltip>
        )}
      </Flex>

      <Text
        fontSize={{ base: '10px', md: '14px' }}
        lineHeight={{ base: '12px', md: '18px' }}
        color={isSelected ? 'white.80' : 'white.60'}
        fontWeight={600}
        textAlign="center"
      >
        {subheading}
      </Text>
    </GridItem>
  );
};

const TyreList = ({
  setSelectedTyres,
  selectedTyres,
  carId,
  setupSuggestions,
  tyreSuggestions,
  raceDifficulty,
  mutateCarPart,
  raceId,
}: {
  setSelectedTyres: Dispatch<SetStateAction<Tyres>>;
  selectedTyres: Tyres;
  carId: string;
  setupSuggestions?: SetupSuggestions;
  tyreSuggestions?: {
    __typename?: "TyreSuggestions";
    lowTierTyres: Array<string>;
    highTierTyres: Array<string>;
  } | undefined;
  raceDifficulty: number;
  mutateCarPart: UseMutateFunction<
    SetCarPartMutation,
    unknown,
    MutationSetCarPartsArgs,
    unknown
  >;
  raceId: string;
}) => {
  const { data: carPartsData, isLoading: isLoadingCarParts } =
    useGetCarParts(carId);

  const { isLoadingTuning } = useTuning();
  const { tuningForRace } = useTuningForRace();
  const { mutateAsync: mutateTuning, isLoading: isLoadingTuningForRace } =
    tuningForRace;

  const [opmitmisticSelectedTyres, setOpmitmisticSelectedTyres] =
    useState<Tyres>(selectedTyres);

  const tyreListRef = useRef<HTMLDivElement>(null);

  const isLoading =
    isLoadingCarParts || isLoadingTuning || isLoadingTuningForRace;
  const tyres =
    carPartsData &&
    carPartsData.getCarParts.find((part) => part.name === 'Tyres');

  const suggestedTyres = raceDifficulty > 2 ? tyreSuggestions?.highTierTyres : tyreSuggestions?.lowTierTyres;

  const tyreIconLookup = {
    C1: IconTyreC1,
    C2: IconTyreC2,
    C3: IconTyreC3,
    C4: IconTyreC4,
    C5: IconTyreC5,
    INTER: IconTyreInter,
    WET: IconTyreWet,
  };

  const getTyreIcon = (TyreId: Tyres) => {
    return tyreIconLookup[TyreId] || <></>;
  };

  const handleChangeTyre = async (tyreId: Tyres) => {
    if (isLoadingTuning) return;
    try {
      await mutateTuning({
        raceTuningInput: {
          raceId,
          tyresId: tyreId,
          carId,
        },
      });
      setOpmitmisticSelectedTyres(tyreId);
      setSelectedTyres(tyreId);
    } catch (error) {
      setOpmitmisticSelectedTyres(opmitmisticSelectedTyres);
      console.error(error);
    }
  };

  // useEffect(() => {
  //   if (!isLoading && tyres && tyreListRef.current) {
  //     tyreListRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  //   }
  // }, [isLoading, tyres]);

  useEffect(() => {
    setOpmitmisticSelectedTyres(selectedTyres);
  }, [selectedTyres]);

  return (
    <Flex ref={tyreListRef} w="full" flexDir="column" gap="0.75rem">
      <Grid
        w="full"
        gridAutoFlow="column"
        gridTemplateColumns="repeat(auto-fit, minmax(0, 1fr))"
        gridAutoColumns="minmax(0, 1fr)"
        gap="0.25rem"
        mb="1.5rem"
      >
        {tyres?.carParts
          .sort((a, b) => a.id.localeCompare(b.id)) // Sorting alphabetically for now
          .map((tyre) => {
            return (
              <TyreCard
                // todo: remove array filter and add locked prop when jeremy addes designs for locked state. temporarily filtering out locked parts.
                key={tyre.id}
                heading={tyre.id}
                subheading={tyre.name}
                icon={getTyreIcon(tyre.id as Tyres)}
                isSelected={opmitmisticSelectedTyres === tyre.id}
                onClick={() => handleChangeTyre(tyre.id as Tyres)}
                opacity={isLoading ? 0.8 : 1}
                cursor={isLoading ? 'not-allowed' : 'pointer'}
                isSuggested={suggestedTyres?.includes(tyre.id as Tyres)}
                label={tyre.description || undefined}
              />
            );
          })}
      </Grid>
    </Flex>
  );
};

export default TyreList;
